import { supabase } from "@/integrations/supabase/client";
import { MenuItem } from "@/types/menu";
import { logMenuItemUpdated } from "@/services/activity/activityLogService";
import { filterValidCategoryIds } from "@/utils/categoryUtils";

export const updateMenuItem = async (
  id: string,
  updates: {
    name?: string;
    description?: string;
    price?: number;
    available?: boolean;
    image_url?: string | null;
    store_id?: string;
  },
  categoryIds: string[],
  tagIds: string[]
): Promise<MenuItem | null> => {
  try {
    // Fetch the existing menu item to compare changes
    const { data: existingItem, error: fetchError } = await supabase
      .from("menu_items")
      .select("*")
      .eq("id", id)
      .single();

    if (fetchError) {
      console.error("Error fetching existing menu item:", fetchError);
      return null;
    }

    // Ensure price is a number if provided
    const updateData = {
      ...updates,
      price:
        typeof updates.price === "string"
          ? parseFloat(updates.price as string)
          : updates.price,
    };

    // Update the menu item - ensure available property is used correctly
    if ("is_available" in updateData) {
      updateData.available = updateData.is_available as boolean;
      delete updateData["is_available"]; // Remove invalid property
    }

    const { data, error } = await supabase
      .from("menu_items")
      .update(updateData)
      .eq("id", id)
      .select()
      .single();

    if (error) {
      console.error("Error updating menu item:", error);
      return null;
    }

    const updatedMenuItem = data as MenuItem;

    // Update categories
    await supabase
      .from("menu_items_categories")
      .delete()
      .eq("menu_item_id", id);

    // Then add new category associations if provided
    let validCategoryIds: string[] = [];
    if (categoryIds && categoryIds.length > 0) {
      // Filter out "all" and any other non-UUID values
      validCategoryIds = filterValidCategoryIds(categoryIds);

      if (validCategoryIds.length > 0) {
        // Get the store_id from the existing menu item
        const { data: menuItem, error: fetchError } = await supabase
          .from("menu_items")
          .select("store_id")
          .eq("id", id)
          .single();

        if (fetchError) {
          console.error("Error fetching menu item for store_id:", fetchError);
        } else if (menuItem) {
          const categoryAssociations = validCategoryIds.map((categoryId) => ({
            menu_item_id: id,
            category_id: categoryId,
            store_id: menuItem.store_id,
          }));

          const { error: categoriesError } = await supabase
            .from("menu_items_categories")
            .insert(categoryAssociations);

          if (categoriesError) {
            console.error(
              "Error updating menu item categories:",
              categoriesError
            );
          }
        }
      }
    }

    // Update tags
    await supabase.from("menu_items_tags").delete().eq("menu_item_id", id);

    // Then add new tag associations if provided
    if (tagIds && tagIds.length > 0) {
      // Get all existing tags that match our names
      const { data: existingTags, error: tagsError } = await supabase
        .from("menu_tags")
        .select("id, name")
        .in("id", tagIds);

      if (tagsError) {
        console.error("Error fetching tag IDs:", tagsError);
      } else {
        // Create a map from tag name to tag ID
        const tagNameToIdMap = new Map();
        existingTags?.forEach((tag) => {
          tagNameToIdMap.set(tag.name, tag.id);
        });

        // Create associations using tag IDs
        const tagAssociations = [];

        for (const tagName of tagIds) {
          const tagId = tagNameToIdMap.get(tagName);
          if (tagId) {
            tagAssociations.push({
              menu_item_id: id,
              tag_id: tagId,
            });
          } else {
            // If tag doesn't exist, create it first
            const { data: newTag, error: createError } = await supabase
              .from("menu_tags")
              .insert({ name: tagName })
              .select()
              .single();

            if (!createError && newTag) {
              tagAssociations.push({
                menu_item_id: id,
                tag_id: newTag.id,
              });
            } else {
              console.error("Error creating new tag:", createError);
            }
          }
        }

        if (tagAssociations.length > 0) {
          const { error: insertError } = await supabase
            .from("menu_items_tags")
            .insert(tagAssociations);

          if (insertError) {
            console.error("Error updating menu item tags:", insertError);
          }
        }
      }
    }

    // Identify what changed
    const changes: Record<string, [unknown, unknown]> = {};

    // Compare fields that might have changed
    if (updates.name !== undefined && existingItem.name !== updates.name) {
      changes.name = [existingItem.name, updates.name];
    }
    if (updates.price !== undefined && existingItem.price !== updates.price) {
      changes.price = [existingItem.price, updates.price];
    }
    if (
      updates.description !== undefined &&
      existingItem.description !== updates.description
    ) {
      changes.description = [existingItem.description, updates.description];
    }
    if (
      updates.image_url !== undefined &&
      existingItem.image_url !== updates.image_url
    ) {
      changes.image_url = [existingItem.image_url, updates.image_url];
    }
    if (
      updates.available !== undefined &&
      existingItem.available !== updates.available
    ) {
      changes.available = [existingItem.available, updates.available];
    }

    // Log the update if there were any changes
    if (Object.keys(changes).length > 0) {
      await logMenuItemUpdated(id, updatedMenuItem.store_id, changes);
    }

    return {
      ...updatedMenuItem,
      categoryIds: validCategoryIds,
      tags: tagIds,
    };
  } catch (error) {
    console.error("Error updating menu item:", error);
    return null;
  }
};
